import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";
import { E3M3_TO_MCF, M3_TO_BBL, MONTH_LENGTH } from "../helpers/conversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsProductionPricesDataTable = ({ economics }) => {

  // STYLES
  const classes = useStyles();
  const currency = economics?.settings?.currency || 'cad';

  const rows = useMemo(() => {
    try {
      let data = [];

      const totalYears = economics?.cashflowGross?.[economics?.cashflowGross?.length - 1]?.year - economics?.cashflowGross?.[0]?.year + 1;
      const residualYears = totalYears - 20;
      const wellType = economics?.economicVolumesGross?.oil > 0 ? 'oil' : 'gas';

      economics.cashflowNet.forEach((cashflow,i) => {
        const year = cashflow?.year;

          data.push({
            id: year,
            year,
            numSources: cashflow?.numSources,
            oilGrossDaily: Number((getVolume(year, 'oil', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            oilGross: getVolume(year, 'oil', 'gross', wellType) || 0,
            oilCompanyGross: getVolume(year, 'oil', 'companyGross', wellType) || 0,
            oilCompanyNet: getVolume(year, 'oil', 'companyNet', wellType) || 0,
            oilPrice: getPrice('oil', year),
            assGasGrossDaily: Number((getVolume(year, 'assGas', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            assGasGross: getVolume(year, 'assGas', 'gross', wellType) || 0,
            assGasCompanyGross: getVolume(year, 'assGas', 'companyGross', wellType) || 0,
            assGasCompanyNet: getVolume(year, 'assGas', 'companyNet', wellType) || 0,
            assGasPrice: getPrice('gas', year),
            solutionGasGrossDaily: Number((getVolume(year, 'solutionGas', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            solutionGasGross: getVolume(year, 'solutionGas', 'gross', wellType) || 0,
            solutionGasCompanyGross: getVolume(year, 'solutionGas', 'companyGross', wellType) || 0,
            solutionGasCompanyNet: getVolume(year, 'solutionGas', 'companyNet', wellType) || 0,
            solutionGasPrice: getPrice('gas', year),
            ethaneGrossDaily: 0,
            ethaneGross: 0,
            ethaneCompanyGross: 0,
            ethaneCompanyNet: 0,
            ethanePrice: 0,
            propaneGrossDaily: Number((getVolume(year, 'propane', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            propaneGross: getVolume(year, 'propane', 'gross', wellType) || 0,
            propaneCompanyGross: getVolume(year, 'propane', 'companyGross', wellType) || 0,
            propaneCompanyNet: getVolume(year, 'propane', 'companyNet', wellType) || 0,
            propanePrice: getPrice('propane', year),
            butaneGrossDaily: Number((getVolume(year, 'butane', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            butaneGross: getVolume(year, 'butane', 'gross', wellType) || 0,
            butaneCompanyGross: getVolume(year, 'butane', 'companyGross', wellType) || 0,
            butaneCompanyNet: getVolume(year, 'butane', 'companyNet', wellType) || 0,
            butanePrice: getPrice('butane', year),
            pentanesGrossDaily: Number((getVolume(year, 'pentanes', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            pentanesGross: getVolume(year, 'pentanes', 'gross', wellType) || 0,
            pentanesCompanyGross: getVolume(year, 'pentanes', 'companyGross', wellType) || 0,
            pentanesCompanyNet: getVolume(year, 'pentanes', 'companyNet', wellType) || 0,
            pentanesPrice: getPrice('pentanes', year),
            condensateGrossDaily: Number((getVolume(year, 'condensate', 'gross', wellType) * 1000 / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
            condensateGross: getVolume(year, 'condensate', 'gross', wellType) || 0,
            condensateCompanyGross: getVolume(year, 'condensate', 'companyGross', wellType) || 0,
            condensateCompanyNet: getVolume(year, 'condensate', 'companyNet', wellType) || 0,
            condensatePrice: getPrice('condensate', year)
          });

      });

      // add subtotal row from first 20 years
      const subtotal = {
        id: 'Subtotal',
        year: 'Subtotal',
        numSources: null,
        oilGrossDaily: null,
        oilGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.oilGross, 0))?.toFixed(2)) || 0,
        oilCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.oilCompanyGross, 0))?.toFixed(2)) || 0,
        oilCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.oilCompanyNet, 0))?.toFixed(2)) || 0,
        oilPrice: null,
        assGasGrossDaily: null,
        assGasGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.assGasGross, 0))?.toFixed(2)) || 0,
        assGasCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.assGasCompanyGross, 0))?.toFixed(2)) || 0,
        assGasCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.assGasCompanyNet, 0))?.toFixed(2)) || 0,
        assGasPrice: null,
        solutionGasGrossDaily: null,
        solutionGasGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.solutionGasGross, 0))?.toFixed(2)) || 0,
        solutionGasCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.solutionGasCompanyGross, 0))?.toFixed(2)) || 0,
        solutionGasCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.solutionGasCompanyNet, 0))?.toFixed(2)) || 0,
        solutionGasPrice: null,
        ethaneGrossDaily: null,
        ethaneGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.ethaneGross, 0))?.toFixed(2)) || 0,
        ethaneCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.ethaneCompanyGross, 0))?.toFixed(2)) || 0,
        ethaneCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.ethaneCompanyNet, 0))?.toFixed(2)) || 0,
        ethanePrice: null,
        propaneGrossDaily: null,
        propaneGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.propaneGross, 0))?.toFixed(2)) || 0,
        propaneCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.propaneCompanyGross, 0))?.toFixed(2)) || 0,
        propaneCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.propaneCompanyNet, 0))?.toFixed(2)) || 0,
        propanePrice: null,
        butaneGrossDaily: null,
        butaneGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.butaneGross, 0))?.toFixed(2)) || 0,
        butaneCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.butaneCompanyGross, 0))?.toFixed(2)) || 0,
        butaneCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.butaneCompanyNet, 0))?.toFixed(2)) || 0,
        butanePrice: null,
        pentanesGrossDaily: null,
        pentanesGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.pentanesGross, 0))?.toFixed(2)) || 0,
        pentanesCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.pentanesCompanyGross, 0))?.toFixed(2)) || 0,
        pentanesCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.pentanesCompanyNet, 0))?.toFixed(2)) || 0,
        pentanesPrice: null,
        condensateGrossDaily: null,
        condensateGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.condensateGross, 0))?.toFixed(2)) || 0,
        condensateCompanyGross: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.condensateCompanyGross, 0))?.toFixed(2)) || 0,
        condensateCompanyNet: Number((data?.slice(0, 20)?.reduce((prev, curr) => prev + curr.condensateCompanyNet, 0))?.toFixed(2)) || 0,
        condensatePrice: null
      };

      // add residual years row
      const residual = {
        id: `${residualYears} Years`,
        year: `${residualYears} Years`,
        numSources: null,
        oilGrossDaily: null,
        oilGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.oilGross, 0))?.toFixed(2)) || 0,
        oilCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.oilCompanyGross, 0))?.toFixed(2)) || 0,
        oilCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.oilCompanyNet, 0))?.toFixed(2)) || 0,
        oilPrice: null,
        assGasGrossDaily: null,
        assGasGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.assGasGross, 0))?.toFixed(2)) || 0,
        assGasCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.assGasCompanyGross, 0))?.toFixed(2)) || 0,
        assGasCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.assGasCompanyNet, 0))?.toFixed(2)) || 0,
        assGasPrice: null,
        solutionGasGrossDaily: null,
        solutionGasGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.solutionGasGross, 0))?.toFixed(2)) || 0,
        solutionGasCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.solutionGasCompanyGross, 0))?.toFixed(2)) || 0,
        solutionGasCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.solutionGasCompanyNet, 0))?.toFixed(2)) || 0,
        solutionGasPrice: null,
        ethaneGrossDaily: null,
        ethaneGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.ethaneGross, 0))?.toFixed(2)) || 0,
        ethaneCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.ethaneCompanyGross, 0))?.toFixed(2)) || 0,
        ethaneCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.ethaneCompanyNet, 0))?.toFixed(2)) || 0,
        ethanePrice: null,
        propaneGrossDaily: null,
        propaneGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.propaneGross, 0))?.toFixed(2)) || 0,
        propaneCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.propaneCompanyGross, 0))?.toFixed(2)) || 0,
        propaneCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.propaneCompanyNet, 0))?.toFixed(2)) || 0,
        propanePrice: null,
        butaneGrossDaily: null,
        butaneGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.butaneGross, 0))?.toFixed(2)) || 0,
        butaneCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.butaneCompanyGross, 0))?.toFixed(2)) || 0,
        butaneCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.butaneCompanyNet, 0))?.toFixed(2)) || 0,
        butanePrice: null,
        pentanesGrossDaily: null,
        pentanesGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.pentanesGross, 0))?.toFixed(2)) || 0,
        pentanesCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.pentanesCompanyGross, 0))?.toFixed(2)) || 0,
        pentanesCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.pentanesCompanyNet, 0))?.toFixed(2)) || 0,
        pentanesPrice: null,
        condensateGrossDaily: null,
        condensateGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.condensateGross, 0))?.toFixed(2)) || 0,
        condensateCompanyGross: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.condensateCompanyGross, 0))?.toFixed(2)) || 0,
        condensateCompanyNet: Number((data?.slice(20, )?.reduce((prev, curr) => prev + curr.condensateCompanyNet, 0))?.toFixed(2)) || 0,
        condensatePrice: null
      };

      // add total row
      const total = {
        id: 'Total',
        year: 'Total',
        numSources: null,
        oilGrossDaily: null,
        oilGross: Number((subtotal?.oilGross + residual?.oilGross)?.toFixed(2)) || 0,
        oilCompanyGross: Number((subtotal?.oilCompanyGross + residual?.oilCompanyGross)?.toFixed(2)) || 0,
        oilCompanyNet: Number((subtotal?.oilCompanyNet + residual?.oilCompanyNet)?.toFixed(2)) || 0,
        oilPrice: null,
        assGasGrossDaily: null,
        assGasGross: Number((subtotal?.assGasGross + residual?.assGasGross)?.toFixed(2)) || 0,
        assGasCompanyGross: Number((subtotal?.assGasCompanyGross + residual?.assGasCompanyGross)?.toFixed(2)) || 0,
        assGasCompanyNet: Number((subtotal?.assGasCompanyNet + residual?.assGasCompanyNet)?.toFixed(2)) || 0,
        assGasPrice: null,
        solutionGasGrossDaily: null,
        solutionGasGross: Number((subtotal?.solutionGasGross + residual?.solutionGasGross)?.toFixed(2)) || 0,
        solutionGasCompanyGross: Number((subtotal?.solutionGasCompanyGross + residual?.solutionGasCompanyGross)?.toFixed(2)) || 0,
        solutionGasCompanyNet: Number((subtotal?.solutionGasCompanyNet + residual?.solutionGasCompanyNet)?.toFixed(2)) || 0,
        solutionGasPrice: null,
        ethaneGrossDaily: null,
        ethaneGross: Number((subtotal?.ethaneGross + residual?.ethaneGross)?.toFixed(2)) || 0,
        ethaneCompanyGross: Number((subtotal?.ethaneCompanyGross + residual?.ethaneCompanyGross)?.toFixed(2)) || 0,
        ethaneCompanyNet: Number((subtotal?.ethaneCompanyNet + residual?.ethaneCompanyNet)?.toFixed(2)) || 0,
        ethanePrice: null,
        propaneGrossDaily: null,
        propaneGross: Number((subtotal?.propaneGross + residual?.propaneGross)?.toFixed(2)) || 0,
        propaneCompanyGross: Number((subtotal?.propaneCompanyGross + residual?.propaneCompanyGross)?.toFixed(2)) || 0,
        propaneCompanyNet: Number((subtotal?.propaneCompanyNet + residual?.propaneCompanyNet)?.toFixed(2)) || 0,
        propanePrice: null,
        butaneGrossDaily: null,
        butaneGross: Number((subtotal?.butaneGross + residual?.butaneGross)?.toFixed(2)) || 0,
        butaneCompanyGross: Number((subtotal?.butaneCompanyGross + residual?.butaneCompanyGross)?.toFixed(2)) || 0,
        butaneCompanyNet: Number((subtotal?.butaneCompanyNet + residual?.butaneCompanyNet)?.toFixed(2)) || 0,
        butanePrice: null,
        pentanesGrossDaily: null,
        pentanesGross: Number((subtotal?.pentanesGross + residual?.pentanesGross)?.toFixed(2)) || 0,
        pentanesCompanyGross: Number((subtotal?.pentanesCompanyGross + residual?.pentanesCompanyGross)?.toFixed(2)) || 0,
        pentanesCompanyNet: Number((subtotal?.pentanesCompanyNet + residual?.pentanesCompanyNet)?.toFixed(2)) || 0,
        pentanesPrice: null,
        condensateGrossDaily: null,
        condensateGross: Number((subtotal?.condensateGross + residual?.condensateGross)?.toFixed(2)) || 0,
        condensateCompanyGross: Number((subtotal?.condensateCompanyGross + residual?.condensateCompanyGross)?.toFixed(2)) || 0,
        condensateCompanyNet: Number((subtotal?.condensateCompanyNet + residual?.condensateCompanyNet)?.toFixed(2)) || 0,
        condensatePrice: null
      };

      data = data.slice(0, 20);
      data.push(subtotal);
      data.push(residual);
      data.push(total);

      return data;


      function getVolume(year, fluid, type, wellType) {
        switch(fluid) {
          case 'oil':
            if (type === 'gross') {
              return Number((economics?.forecastGross?.byFluid?.oil?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.forecastNet?.byFluid?.oil?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.forecastCompanyNet?.byFluid?.oil?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'solutionGas':
            if (wellType === 'oil') {
              if (type === 'gross') {
                return Number((economics?.forecastGross?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              } else if (type === 'companyGross') {
                return Number((economics?.forecastNet?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              } else if (type === 'companyNet') {
                return Number((economics?.forecastCompanyNet?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              };
              break;
            } else {
              return 0;
            };
          case 'assGas':
            if (wellType === 'gas') {
              if (type === 'gross') {
                return Number((economics?.forecastGross?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              } else if (type === 'companyGross') {
                return Number((economics?.forecastNet?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              } else if (type === 'companyNet') {
                return Number((economics?.forecastCompanyNet?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 1000)?.toFixed(2)) || 0;
              };
              break;
            } else {
              return 0;
            };
          case 'propane':
            if (type === 'gross') {
              return Number((economics?.forecastGross?.byFluid?.propane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.forecastNet?.byFluid?.propane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.forecastCompanyNet?.byFluid?.propane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'butane':
            if (type === 'gross') {
              return Number((economics?.forecastGross?.byFluid?.butane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.forecastNet?.byFluid?.butane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.forecastCompanyNet?.byFluid?.butane?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'pentanes':
            if (type === 'gross') {
              return Number((economics?.forecastGross?.byFluid?.pentanes?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.forecastNet?.byFluid?.pentanes?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.forecastCompanyNet?.byFluid?.pentanes?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'condensate':
            if (type === 'gross') {
              return Number((economics?.forecastGross?.byFluid?.condensate?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.forecastNet?.byFluid?.condensate?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.forecastCompanyNet?.byFluid?.condensate?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 1000)?.toFixed(2)) || 0;
            };
            break;
          default:
            return 0;
        };
      };

      function getPrice(fluid, year) {
        switch(fluid) {
          case 'oil':
            return economics?.settings?.priceOil?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          case 'gas':
            return economics?.settings?.priceGas?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          case 'propane':
            return economics?.settings?.pricePropane?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          case 'butane':
            return economics?.settings?.priceButane?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          case 'pentanes':
            return economics?.settings?.pricePentanes?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          case 'condensate':
            return economics?.settings?.priceCondensate?.find(p => new Date(p.date)?.getFullYear() === year)?.price;
          default:
            return 0;
        }
        
      };

    } catch (error) {
    };
  }, [economics]);

  const columns = useMemo(() => {
    return [
      {
        field: 'year',
        headerName: 'Year',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'numSources',
        headerName: 'Gr. Well',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilGrossDaily',
        headerName: 'Light & Medium Oil Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilGross',
        headerName: 'Light & Medium Oil Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilCompanyGross',
        headerName: 'Light & Medium Oil Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilCompanyNet',
        headerName: 'Light & Medium Oil Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilPrice',
        headerName: `Oil Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasGrossDaily',
        headerName: 'Gas Gross Daily (Mscf/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasGross',
        headerName: 'Gas Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasCompanyGross',
        headerName: 'Gas Comp. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasCompanyNet',
        headerName: 'Gas Comp. Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasPrice',
        headerName: `Gas Price (${currency === 'cad' ? 'CAD' : 'USD'}/Mscf)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasGrossDaily',
        headerName: 'Solution Gas Gross Daily (Mscf/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasGross',
        headerName: 'Solution Gas Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasCompanyGross',
        headerName: 'Solution Gas Comp. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasCompanyNet',
        headerName: 'Solution Gas Comp. Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasPrice',
        headerName: `Solution Gas Price (${currency === 'cad' ? 'CAD' : 'USD'}/Mscf)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'ethaneGrossDaily',
        headerName: 'Ethane Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'ethaneGross',
        headerName: 'Ethane Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'ethaneCompanyGross',
        headerName: 'Ethane Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'ethaneCompanyNet',
        headerName: 'Ethane Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'ethanePrice',
        headerName: `Ethane Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propaneGrossDaily',
        headerName: 'Propane Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propaneGross',
        headerName: 'Propane Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propaneCompanyGross',
        headerName: 'Propane Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propaneCompanyNet',
        headerName: 'Propane Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propanePrice',
        headerName: `Propane Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butaneGrossDaily',
        headerName: 'Butane Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butaneGross',
        headerName: 'Butane Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butaneCompanyGross',
        headerName: 'Butane Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butaneCompanyNet',
        headerName: 'Butane Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butanePrice',
        headerName: `Butane Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesGrossDaily',
        headerName: 'Pentanes+ Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesGross',
        headerName: 'Pentanes+ Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesCompanyGross',
        headerName: 'Pentanes+ Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesCompanyNet',
        headerName: 'Pentanes+ Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesPrice',
        headerName: `Pentanes+ Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateGrossDaily',
        headerName: 'Condensate Gross Daily (bbl/d)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateGross',
        headerName: 'Condensate Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateCompanyGross',
        headerName: 'Condensate Comp. Gross (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateCompanyNet',
        headerName: 'Condensate Comp. Net (Mbbl)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensatePrice',
        headerName: `Condensate Price (${currency === 'cad' ? 'CAD' : 'USD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      }
    ];
  }, [currency]);

  try {

    if (rows) {
      return (
        <div className={classes.root}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            disableSelectionOnClick
            className={classes.table}
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{fontSize: 12}}
            rowHeight={24}
            autoHeight={true}
          />
        </div>
      );
    } else {
      return <></>;
    };

  } catch (error) {
    return <></>
  };

};
